import { VisibilityOff } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, MenuItem, TextField, styled } from "@mui/material";
import { formatDateDayjs } from "@qubit/autoparts";
import dayjs from "dayjs";

import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import { Inv_InventoryDateLabelSettings } from "~/config/clientConfig";
import InventoryDateField from "~/features/autostorePutaway/InventoryDateField";
import { setIsInventoryDateFieldDirty } from "~/features/cycleCounts/cycleCounts.slice";

import { QUANTITY_MAX_VALUE } from "~/lib/shared";
import { generateInventoryReasonCodeOptions } from "~/redux/actions/inventory";
import { selectClientSpecificInventoryAdjustmentReasonCodes } from "~/redux/selectors/storeSelectors";
import { InventoryDto, VariantFrontendDto } from "~/types/api";

import {
  DEFAULT_REASON_CODE,
  setReasonCode,
  setIsQuantityDirty
} from "./cycleCountsNew.slice";

const PickInfoFieldsContainer = styled(Box)`
  display: grid;
  grid-template-columns: 210px 1fr;
  gap: 16px;
  align-items: center;
  margin-top: 20px;
`;

const AdjustQuantityButton = styled(IconButton)(({ theme }) => ({
  color: "black",
  backgroundColor: theme.palette.gray.dark,
  borderRadius: "10px",
  ":hover": {
    backgroundColor: theme.palette.darkGray.light
  }
}));

function CountingTaskInventoryCard(props: {
  selectedVariant: VariantFrontendDto;
  inv_inventoryDateLabel: Inv_InventoryDateLabelSettings;
  inventoryDate: Date | string | null;
  setInventoryDate: Dispatch<SetStateAction<string | null>>;
  selectedInventoryRecord: InventoryDto | null;
  changedQuantity: number | undefined;
  onClickIncreaseQuantity: (qty: number) => void;
  onClickDecreaseQuantity: (qty: number) => void;
  handleChangeQuantity: (qty: number) => void;
  isDateValid: boolean;
  isCycleCountBlind?: boolean;
}) {
  const {
    selectedVariant,
    inv_inventoryDateLabel,
    inventoryDate,
    setInventoryDate,
    selectedInventoryRecord,
    changedQuantity,
    // form functions
    onClickIncreaseQuantity,
    onClickDecreaseQuantity,
    handleChangeQuantity,
    isDateValid,
    isCycleCountBlind = false
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [showBlindIcon, setShowBlindIcon] = useState<boolean>(true);

  const isInventoryDateFieldDirty = useAppSelector(
    (state) => state.cycleCountsSlice.isInventoryDateFieldDirty
  );
  const clientFcReasonCodes = useAppSelector(
    selectClientSpecificInventoryAdjustmentReasonCodes
  );
  const reasonCode = useAppSelector(
    (state) => state.cycleCountsNewSlice.reasonCode
  );
  const isQuantityDirty = useAppSelector(
    (state) => state.cycleCountsNewSlice.isQuantityDirty
  );

  const getInventoryDate = () => {
    if (isInventoryDateFieldDirty) {
      return dayjs(inventoryDate);
    }
    switch (inv_inventoryDateLabel) {
      case "expiration":
        return dayjs(selectedInventoryRecord?.expiration) || null;
      case "manufacture":
        return dayjs(selectedInventoryRecord?.manufactureDate) || null;
      default:
        return null;
    }
  };

  useEffect(() => {
    dispatch(setIsInventoryDateFieldDirty(false));
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setShowBlindIcon(true);
    dispatch(setReasonCode(DEFAULT_REASON_CODE));
    dispatch(setIsQuantityDirty(false));
  }, [selectedInventoryRecord?.inventoryId, dispatch]);

  const clearReasonCode = () => {
    dispatch(setIsQuantityDirty(true));
  };

  const handleQuantityChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const inputValue = parseInt(e.target.value, 10);
    if (inputValue < 0 || inputValue > QUANTITY_MAX_VALUE) return;
    clearReasonCode();
    handleChangeQuantity(inputValue);
  };

  const handleIncreaseQuantity = (qty: number) => {
    clearReasonCode();
    onClickIncreaseQuantity(qty);
  };

  const handleDecreaseQuantity = (qty: number) => {
    clearReasonCode();
    onClickDecreaseQuantity(qty);
  };

  const initialQty = isCycleCountBlind
    ? 0
    : selectedInventoryRecord?.count?.value;

  const displayQty = changedQuantity ?? initialQty;

  const isReasonCodeError =
    (isQuantityDirty || isInventoryDateFieldDirty) && !reasonCode;

  return (
    <ProductCard.Root
      productName={selectedVariant.productName}
      imageFileName={selectedVariant.imageFilename}
      tempZone={selectedVariant.temperatureZone}
      sx={{ pb: 0 }}
      bottomSection={
        <PickInfoFieldsContainer>
          <InventoryDateField
            inventoryDate={getInventoryDate()}
            inv_inventoryDateLabel={inv_inventoryDateLabel}
            isPastDateAllowed
            isDateValid={isDateValid}
            setInventoryDate={(date) => {
              setInventoryDate(formatDateDayjs(date));
            }}
            onChangeCallback={() => {
              dispatch(setIsInventoryDateFieldDirty(true));
            }}
          />

          <Box sx={{ display: "flex", gap: "8px" }}>
            <Box sx={{ position: "relative" }}>
              <TextField
                label={t("quantity")}
                variant="outlined"
                inputProps={{
                  style: {
                    fontSize: "48px",
                    textAlign: "center"
                  },
                  type: "number", // Set input type to number
                  pattern: "[0-9]*", // Add pattern to accept only numbers
                  inputMode: "numeric" // Set input mode to numeric for better mobile support
                }}
                InputLabelProps={{
                  sx: {
                    color: "gray.main.dark",
                    fontSize: 18
                  },
                  shrink: true
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "2px"
                  }
                }}
                onChange={(e) => handleQuantityChange(e)}
                value={displayQty}
                error={
                  (!displayQty && displayQty !== 0) ||
                  (changedQuantity && changedQuantity < 0) ||
                  !selectedInventoryRecord?.count?.value
                }
              />
              {isCycleCountBlind &&
                !changedQuantity &&
                changedQuantity !== 0 &&
                showBlindIcon && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "0",
                      bottom: "0",
                      margin: "auto",
                      left: "0",
                      right: "0",
                      width: "fit-content",
                      height: "fit-content",
                      padding: "15px",
                      background: "white"
                    }}
                  >
                    <IconButton onClick={() => setShowBlindIcon(false)}>
                      <VisibilityOff fontSize="large" />
                    </IconButton>
                  </Box>
                )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "6px"
              }}
            >
              <AdjustQuantityButton
                size="large"
                aria-label="increase quantity"
                onClick={() => handleIncreaseQuantity(displayQty ?? 0)}
              >
                <KeyboardArrowUpIcon />
              </AdjustQuantityButton>
              <AdjustQuantityButton
                size="large"
                aria-label="decrease quantity"
                onClick={() => handleDecreaseQuantity(displayQty ?? 0)}
                disabled={!selectedInventoryRecord?.count?.value}
              >
                <KeyboardArrowDownIcon />
              </AdjustQuantityButton>
            </Box>
          </Box>

          <TextField
            label={t("reason code")}
            select
            fullWidth
            variant="outlined"
            value={reasonCode}
            disabled={!isQuantityDirty && !isInventoryDateFieldDirty}
            error={isReasonCodeError}
            helperText={
              isReasonCodeError
                ? `* ${t("required when adjusting quantity or date")}`
                : null
            }
            slotProps={{
              inputLabel: { shrink: true },
              select: {
                onChange: (e) =>
                  dispatch(setReasonCode(e.target.value as string))
              }
            }}
            sx={{
              gridColumn: "1 / -1",
              minHeight: "83px" // Avoid layout shift when error is shown
            }}
          >
            {generateInventoryReasonCodeOptions(clientFcReasonCodes, true).map(
              (option, i, arr) => (
                <MenuItem
                  key={`reason-code-option-${i}`}
                  sx={{
                    padding: 1,
                    fontSize: "24px",
                    borderBottomColor: "gray.light",
                    borderBottom: i === arr.length - 1 ? "none" : `1px solid`
                  }}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              )
            )}
          </TextField>
        </PickInfoFieldsContainer>
      }
      disableGutters
    >
      <ProductCard.Sku sku={selectedVariant.sku} />
      <ProductCard.UpcList upcs={selectedVariant.allUpcs} />
    </ProductCard.Root>
  );
}

export default CountingTaskInventoryCard;
